import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Paper,
} from "@mui/material";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const colors = {
  text: "#202124",
  muted: "#5f6368",
  blue: "#1a73e8",
  green: "#34a853",
  yellow: "#fbbc04",
  red: "#ea4335",
  border: "#dadce0",
  soft: "#f8fafd",
};

const cardSx = {
  height: "100%",
  borderRadius: 5,
  border: `1px solid ${colors.border}`,
  boxShadow: "0 1px 2px rgba(60,64,67,.08), 0 2px 6px rgba(60,64,67,.06)",
  transition: "transform .2s ease, box-shadow .2s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 24px rgba(60,64,67,.14)",
  },
};

const SectionTitle = ({ eyebrow, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ color: colors.blue, fontWeight: 700, fontSize: 14, mb: 1 }}>
      {eyebrow}
    </Typography>
    <Typography variant="h4" sx={{ color: colors.text, fontWeight: 800, letterSpacing: "-0.03em" }}>
      {title}
    </Typography>
    {children && (
      <Typography sx={{ color: colors.muted, mt: 1.2, maxWidth: 820, lineHeight: 1.75 }}>
        {children}
      </Typography>
    )}
  </Box>
);

export default function Landing() {
  const nav = useMemo(
    () => [
      { label: "Inicio", id: "top" },
      { label: "Grupo", id: "group" },
      { label: "Líneas", id: "lines" },
      { label: "Capacidades", id: "capabilities" },
      { label: "Aplicaciones", id: "applications" },
      { label: "Proyectos", id: "projects" },
      { label: "Equipo", id: "team" },
      { label: "Noticias", id: "news" },
      { label: "Contacto", id: "contact" },
    ],
    []
  );

  const researchLines = [
    {
      title: "Redes IoT y sistemas distribuidos",
      text: "Evaluación y mejora de tecnologías inalámbricas, redes LPWAN y comunicaciones en escenarios urbanos, rurales y costeros.",
      color: colors.blue,
    },
    {
      title: "Comunicaciones inalámbricas avanzadas",
      text: "Diseño y análisis de comunicaciones de corto y largo alcance para entornos heterogéneos y con restricciones operativas.",
      color: colors.green,
    },
    {
      title: "Edge AI y procesamiento en el borde",
      text: "Modelos de inteligencia artificial distribuida para análisis en tiempo real, reducción de latencia y optimización de recursos.",
      color: colors.yellow,
    },
    {
      title: "Ciberseguridad en redes y sistemas",
      text: "Detección de anomalías, protección de infraestructuras conectadas y respuesta ante incidentes en ecosistemas IoT.",
      color: colors.red,
    },
    {
      title: "Aplicaciones IoT y smart environments",
      text: "Integración de sistemas de sensorización, monitorización ambiental, infraestructuras inteligentes y entornos costeros.",
      color: colors.blue,
    },
  ];

  const capabilities = [
    "Diseño de soluciones IoT completas: sensores, nodos embebidos, gateways y plataformas edge/cloud.",
    "Optimización de eficiencia energética, escalabilidad, calidad de servicio, seguridad y tiempo real.",
    "Modelado, simulación y experimentación con bancos de prueba, Raspberry Pi, ESP32, cámaras inteligentes y redes.",
  ];

  const applications = [
    "Monitorización ambiental: aire, agua, ruido y datos en tiempo real",
    "Medio marino y costero, puertos, transporte marítimo y economía azul",
    "Agricultura inteligente, smart cities, industria 4.0 e infraestructuras críticas",
    "Videovigilancia inteligente, detección de eventos, anomalías y ciberataques",
    "Gemelos digitales y sistemas inteligentes conectados",
    "Aplicaciones llevables para monitorización de enfermedades",
  ];

  const keywords = [
    "IoT",
    "Edge computing",
    "Inteligencia artificial",
    "Ciberseguridad",
    "Redes inalámbricas",
    "LPWAN",
    "Sistemas distribuidos",
    "Sensorización",
    "Smart environments",
    "Tiempo real",
  ];

  const team = [
    {
      group: "Responsable de la línea",
      people: [
        {
          name: "Sendra Compte, Sandra",
          role: "Responsable de la línea",
          email: "sansenco@upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=emSqcUQAAAAJ",
        },
      ],
    },
    {
      group: "Equipo investigador",
      people: [
        {
          name: "Lloret Mauri, Jaime",
          role: "Profesor Catedrático de Universidad",
          email: "jlloret[at]dcom.upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&oi=ao&user=ZJYUEGEAAAAJ",
        },
        {
          name: "Tomás Gironés, Jesus",
          role: "Profesor Titular de Universidad",
          email: "jtomas[at]upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&user=qjl9Xg4AAAAJ",
        },
        {
          name: "Romero Martínez, José Oscar",
          role: "Profesor Titular de Universidad",
          email: "oromero[at]dcom.upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&user=U1lvN-cAAAAJ",
        },
        {
          name: "Jiménez Herranz, José Miguel",
          role: "Profesor Asociado",
          email: "jojiher[at]dcom.upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=OcStz3wAAAAJ",
        },
        {
          name: "Sandra Viciano Tudela",
          role: "Investigadora Predoctoral",
          email: "svictud[at]upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&user=A4xhpnkAAAAJ",
        },
        {
          name: "Aika Miura",
          role: "Investigadora Predoctoral",
          email: "aikasmiura[at]gmail.com",
          scholar: "https://scholar.google.com/citations?user=VO2j_sAAAAAJ",
        },
        {
          name: "Diaz Blasco, Francisco Javier",
          role: "Investigador Predoctoral",
          email: "fjdiabla[at]doctor.upv.es",
          scholar: "https://scholar.google.es/citations?user=eG0aF8AAAAAJ",
        },
        {
          name: "Ahmad, Ali",
          role: "Investigador Predoctoral",
          email: "aahmad1[at]upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=lqqXBtcAAAAJ",
        },
        {
          name: "Arman Foroughi",
          role: "Investigador Predoctoral",
          email: "foroughiarman7[at]gmail.com",
          scholar: "https://scholar.google.com/citations?hl=en&user=3z41XDEAAAAJ",
        },
        {
          name: "Ivars Palomares, Alberto",
          role: "Investigador Predoctoral",
          email: "aivapal[at]epsg.upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&user=ubIP6dkAAAAJ",
        },
        {
          name: "Zaragoza Esquerdo, Miguel",
          role: "Investigador Predoctoral",
          email: "mizaes2[at]epsg.upv.es",
          scholar: "https://scholar.google.es/citations?hl=es&user=7qr4MrcAAAAJ",
        },
        {
          name: "Iman Valizadeh",
          role: "Investigador Predoctoral",
          email: "ivaliza[at]doctor.upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=DQqBtaAAAAAJ",
        },
        {
          name: "Daniel Matheus Brandão Lent",
          role: "Investigador Predoctoral",
          email: "matheus.daniel[at]uel.br",
          scholar: "https://scholar.google.com.br/citations?hl=en&user=r6N_Iu8AAAAJ",
        },
        {
          name: "Samira Amiri",
          role: "Investigadora Predoctoral",
          email: "samiri[at]doctor.upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=9WVjpeoAAAAJ",
        },
        {
          name: "Arman Heydari",
          role: "Investigador Predoctoral",
          email: "arman.1996.heydari[at]gmail.com",
          scholar: "",
        },
        {
          name: "Kara Kübra",
          role: "PhD researcher at UPV",
          email: "kkubra[at]doctor.upv.es",
          scholar: "https://scholar.google.com/citations?hl=en&user=Ej_z_aEAAAAJ",
        },
      ],
    },
  ];

  const news = [
    {
      title: "Investigadores de la UPV diseñan una aplicación para el diagnóstico autónomo de patologías en los naranjos",
      source: "Valencia Plaza",
      url: "https://valenciaplaza.com/valenciaplaza/educacion/investigadores-de-la-upv-disenan-una-aplicacion-para-el-diagnostico-autonomo-de-patologias-en-los-naranjos",
    },
    {
      title: "Profesores del Campus de Gandia digitalizan la agricultura para mejorar la productividad y competitividad de los cultivos",
      source: "COPE Onda Naranja",
      url: "https://ondanaranjacope.com/noticias/profesores-del-campus-de-gandia-digitalizan-la-agricultura-para-mejorar-la-productividad-y-competitividad-de-los-cultivos",
    },
    {
      title: "Las picaduras de medusa pueden tener los días contados",
      source: "Cadena SER",
      url: "https://cadenaser.com/comunitat-valenciana/2026/06/26/las-picaduras-de-medusa-pueden-tener-los-dias-contados-radio-gandia/",
    },
  ];

  return (
    <Box id="top" sx={{ minHeight: "100vh", bgcolor: "#fff", color: colors.text }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,.88)",
          color: colors.text,
          borderBottom: `1px solid ${colors.border}`,
          backdropFilter: "blur(14px)",
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: 68 }}>
          <Box
            onClick={() => scrollToId("top")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              mr: 2,
              cursor: "pointer",
              minWidth: { xs: "auto", md: 250 },
            }}
          >
            <Typography sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              Comunicaciones y Redes
            </Typography>
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.25, flex: 1 }}>
            {nav.map((n) => (
              <Button
                key={n.id}
                onClick={() => scrollToId(n.id)}
                sx={{
                  textTransform: "none",
                  color: colors.muted,
                  borderRadius: 99,
                  px: 1.6,
                  "&:hover": { bgcolor: "#f1f3f4", color: colors.text },
                }}
              >
                {n.label}
              </Button>
            ))}
          </Box>

          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            disableElevation
            sx={{
              textTransform: "none",
              borderRadius: 99,
              bgcolor: colors.blue,
              px: 2.4,
              fontWeight: 700,
              "&:hover": { bgcolor: "#185abc" },
            }}
          >
            Acceder
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          background:
            "radial-gradient(circle at 15% 10%, rgba(66,133,244,.16), transparent 30%), radial-gradient(circle at 85% 20%, rgba(52,168,83,.14), transparent 30%), linear-gradient(180deg, #fff 0%, #f8fafd 100%)",
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="IGIC · Universitat Politècnica de València"
                sx={{ bgcolor: "#e8f0fe", color: colors.blue, fontWeight: 700, mb: 2, borderRadius: 99 }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.055em",
                  lineHeight: 1.02,
                  color: colors.text,
                  maxWidth: 820,
                }}
              >
                Comunicaciones y Redes: redes, IOT, ciberseguridad y multimedia
              </Typography>
              <Typography sx={{ mt: 2.5, color: colors.muted, maxWidth: 760, fontSize: 18, lineHeight: 1.8 }}>
                El grupo Comunicaciones y Redes (IGIC-UPV) diseña, analiza e implementa sistemas avanzados de comunicaciones,
                redes inteligentes y soluciones digitales distribuidas, integrando telecomunicaciones, informática e inteligencia artificial.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  onClick={() => scrollToId("group")}
                  variant="contained"
                  disableElevation
                  sx={{ textTransform: "none", borderRadius: 99, px: 3, py: 1.2, bgcolor: colors.blue, fontWeight: 700 }}
                >
                  Conocer el grupo
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  sx={{ textTransform: "none", borderRadius: 99, px: 3, py: 1.2, borderColor: colors.border, color: colors.blue, fontWeight: 700 }}
                >
                  Acceder al panel
                </Button>
                <Button
                  component={RouterLink}
                  to="/map"
                  sx={{ textTransform: "none", borderRadius: 99, px: 2, py: 1.2, color: colors.muted }}
                >
                  Ver mapa
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 7,
                  p: 1.2,
                  border: `1px solid ${colors.border}`,
                  bgcolor: "rgba(255,255,255,.82)",
                  boxShadow: "0 20px 70px rgba(60,64,67,.14)",
                }}
              >
                <Box sx={{ p: 3, borderRadius: 6, bgcolor: "#fff" }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                    Cadena tecnológica completa
                  </Typography>
                  {[
                    ["Sensores y dispositivos", "Nodos embebidos, gateways, cámaras inteligentes y bancos de prueba IoT."],
                    ["Comunicaciones", "Redes inalámbricas, LPWAN, protocolos, calidad de servicio y conectividad limitada."],
                    ["Datos e inteligencia", "Edge/cloud, machine learning, detección de eventos y análisis en tiempo real."],
                  ].map(([title, text], index) => (
                    <Box key={title} sx={{ display: "flex", gap: 1.5, mb: index === 2 ? 0 : 2.2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", mt: 0.9, bgcolor: [colors.blue, colors.green, colors.yellow][index] }} />
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
                        <Typography sx={{ color: colors.muted, lineHeight: 1.6 }}>{text}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }} id="group">
        <SectionTitle eyebrow="Grupo de investigación" title="Comunicaciones y Redes · IGIC-UPV">
          Especializado en comunicaciones avanzadas, redes y sistemas inteligentes, con aplicación en IoT, edge computing,
          sistemas distribuidos, entornos costeros y transferencia tecnológica hacia sectores productivos.
        </SectionTitle>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={8}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Investigación aplicada con visión de sistema completo
                </Typography>
                <Typography sx={{ color: colors.muted, lineHeight: 1.85 }}>
                  La actividad del grupo cubre desde el nivel físico —sensores, dispositivos embebidos y comunicaciones inalámbricas—
                  hasta capas superiores de procesamiento de datos, inteligencia artificial, servicios digitales y plataformas conectadas.
                  Su enfoque permite desarrollar soluciones completas para investigación aplicada, validación experimental y transferencia.
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  {keywords.map((k) => (
                    <Chip key={k} label={k} sx={{ bgcolor: "#f1f3f4", color: colors.text, fontWeight: 600 }} />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ ...cardSx, bgcolor: "#f8fafd" }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                  Enfoque principal
                </Typography>
                <Stack spacing={1.6}>
                  {["IoT y LPWAN", "Edge AI", "Ciberseguridad", "Smart environments", "Medio marino y costero"].map((item) => (
                    <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colors.blue }} />
                      <Typography sx={{ color: colors.text, fontWeight: 650 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: colors.soft, py: 8 }} id="lines">
        <Container maxWidth="lg">
          <SectionTitle eyebrow="Líneas de investigación" title="Redes, IA distribuida y seguridad">
            Líneas alineadas con ingeniería de telecomunicación, redes de comunicaciones, sistemas inteligentes basados en datos y aplicaciones IoT.
          </SectionTitle>

          <Grid container spacing={2.5}>
            {researchLines.map((line) => (
              <Grid item xs={12} md={line.title.includes("Aplicaciones") ? 8 : 4} key={line.title}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 3.2 }}>
                    <Box sx={{ width: 36, height: 5, borderRadius: 99, bgcolor: line.color, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                      {line.title}
                    </Typography>
                    <Typography sx={{ color: colors.muted, lineHeight: 1.7 }}>{line.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }} id="capabilities">
        <SectionTitle eyebrow="Capacidades científico-técnicas" title="Diseño e integración de sistemas complejos inteligentes">
          El grupo desarrolla soluciones conectadas fiables, escalables y seguras para entornos reales con restricciones de latencia,
          conectividad, consumo energético y condiciones ambientales adversas.
        </SectionTitle>

        <Grid container spacing={2.5}>
          {capabilities.map((cap, index) => (
            <Grid item xs={12} md={4} key={cap}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 3.2 }}>
                  <Typography sx={{ color: [colors.blue, colors.green, colors.red][index], fontWeight: 900, fontSize: 30, mb: 1 }}>
                    0{index + 1}
                  </Typography>
                  <Typography sx={{ color: colors.muted, lineHeight: 1.75 }}>{cap}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: colors.soft, py: 8 }} id="applications">
        <Container maxWidth="lg">
          <SectionTitle eyebrow="Aplicaciones" title="Tecnología transferible a sectores reales">
            Aplicaciones en monitorización continua, toma de decisiones en tiempo real, reducción de costes operativos y digitalización de infraestructuras.
          </SectionTitle>

          <Grid container spacing={1.5}>
            {applications.map((app, index) => (
              <Grid item xs={12} sm={6} md={4} key={app}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.2,
                    height: "100%",
                    borderRadius: 4,
                    border: `1px solid ${colors.border}`,
                    bgcolor: "#fff",
                    display: "flex",
                    gap: 1.4,
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ width: 9, height: 9, mt: 0.8, borderRadius: "50%", bgcolor: [colors.blue, colors.red, colors.yellow, colors.green][index % 4] }} />
                  <Typography sx={{ color: colors.text, fontWeight: 650, lineHeight: 1.55 }}>{app}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }} id="projects">
        <SectionTitle eyebrow="Proyectos y plataforma" title="Observatorios IoT, datos y visualización">
          La web mantiene una landing pública para difusión científica y un panel privado para visualización, mapas, tablas, exportación y seguimiento de nodos.
        </SectionTitle>

        <Grid container spacing={2.5}>
          {[
            ["GRIDSEA", "Observatorio marino-costero con sensores multiparamétricos, comunicaciones y gemelo digital."],
            ["ThinkInAzul", "Monitorización inteligente, transferencia tecnológica y soporte a la toma de decisiones en economía azul."],
            ["Agricultura 6.0", "Redes de sensores, visión artificial y modelos predictivos para agricultura inteligente."],
          ].map(([title, text]) => (
            <Grid item xs={12} md={4} key={title}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 3.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
                  <Typography sx={{ color: colors.muted, mt: 1.2, lineHeight: 1.7 }}>{text}</Typography>
                  <Button onClick={() => scrollToId("applications")} sx={{ mt: 2.2, textTransform: "none", borderRadius: 99, color: colors.blue, fontWeight: 700 }}>
                    Ver aplicaciones
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Container maxWidth="lg" sx={{ py: 8 }} id="team">
        <SectionTitle eyebrow="Equipo" title="Equipo investigador">
          Responsable de la línea y miembros del grupo Comunicaciones y Redes.
        </SectionTitle>

        {team.map((block) => (
          <Box key={block.group} sx={{ mb: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              {block.group}
            </Typography>

            <Grid container spacing={2.5}>
              {block.people.map((person) => (
                <Grid item xs={12} sm={6} md={4} key={person.email}>
                  <Card sx={cardSx}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {person.name}
                      </Typography>
                      <Typography sx={{ color: colors.muted, mt: 0.5 }}>
                        {person.role}
                      </Typography>
                      <Button
                        component="a"
                        href={`mailto:${person.email}`}
                        sx={{ mt: 1.5, textTransform: "none", color: colors.blue, fontWeight: 700 }}
                      >
                        {person.email}
                      </Button>
                      <br />
                      <Button
                        component="a"
                        href={person.scholar || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: "none", color: colors.muted }}
                      >
                        Google Scholar
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>

      <Box sx={{ bgcolor: colors.soft, py: 8 }} id="news">
        <Container maxWidth="lg">
          <SectionTitle eyebrow="Noticias" title="Noticias y difusión">
            Apariciones recientes del grupo en medios de comunicación.
          </SectionTitle>

          <Grid container spacing={2.5}>
            {news.map((item) => (
              <Grid item xs={12} md={4} key={item.url}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 3.2 }}>
                    <Chip label={item.source} sx={{ mb: 2, bgcolor: "#e8f0fe", color: colors.blue, fontWeight: 700 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.35 }}>
                      {item.title}
                    </Typography>
                    <Button
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 2, textTransform: "none", color: colors.blue, fontWeight: 700 }}
                    >
                      Leer noticia
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Box sx={{ bgcolor: "#fff", py: 8 }} id="contact">
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              borderRadius: 7,
              border: `1px solid ${colors.border}`,
              p: { xs: 3, md: 5 },
              background:
                "linear-gradient(135deg, rgba(232,240,254,.95), rgba(255,255,255,1) 55%, rgba(232,245,233,.9))",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography sx={{ color: colors.blue, fontWeight: 800, mb: 1 }}>Contacto y colaboración</Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.035em" }}>
                  Colaboración en I+D aplicada, prototipado, validación y transferencia tecnológica.
                </Typography>
                <Typography sx={{ color: colors.muted, mt: 1.5, lineHeight: 1.75 }}>
                  Responsable de la línea: Sandra Sendra Compte · sansenco[at]upv.es

                  Colaboración científica: Jaime Lloret Mauri · jlloret[at]dcom.upv.es

                  Webmaster: Miguel Zaragoza Esquerdo · mizaes2[at]epsg.upv.es
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1.3} alignItems={{ xs: "stretch", md: "flex-end" }}>
                  <Button component={RouterLink} to="/login" variant="contained" disableElevation sx={{ textTransform: "none", borderRadius: 99, bgcolor: colors.blue, px: 3, py: 1.2, fontWeight: 800 }}>
                    Acceder al panel
                  </Button>
                  <Button onClick={() => scrollToId("top")} variant="outlined" sx={{ textTransform: "none", borderRadius: 99, borderColor: colors.border, color: colors.text, px: 3, py: 1.2 }}>
                    Volver arriba
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ py: 3.5, borderTop: `1px solid ${colors.border}`, textAlign: "center", color: colors.muted, bgcolor: "#fff" }}>
        © UPV · IGIC · Comunicaciones y Redes ·{" "}
        <Button
          component="a"
          href="https://igic.webs.upv.es/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none", color: colors.blue, fontWeight: 700 }}
        >
          Web IGIC
        </Button>
      </Box>
    </Box>
  );
}